/**
 * L'Atelier — conteneur de la séquence de production (SPEC-UI §3.3).
 *
 * Séquence **short** : Écriture → Tournage → Montage (un short est son propre clip : pas de
 * miniature, pas de promotion — PLAN §4.5). Séquence **longue** : les cinq.
 *
 * Chaque étape enchaîne trois phases : **intro** (la règle en une phrase, les assists actifs,
 * `Jouer`) → **jeu** → **score**. À la fin : récap, puis **une seule** action `produce_video`
 * avec tout ce qu'il faut (modèle one-shot, arbitrage A14), puis l'écran de résultat — courbe
 * animée, classe révélée.
 *
 * ⟦C62 · lot atelier · friction 3⟧ **Vu du joueur, deux écrans par étape, deux clics** : la consigne du mini-jeu
 * (la réplique de Matt y tient, la première fois, la place de la phrase de règle) avec UN « Jouer », puis, après le
 * jeu, son écran de fin avec UN « Suivant » (qui garde le score ET passe à l'étape d'après). Les phases `intro` et
 * `score` du store existent toujours, mais l'Atelier ne s'arrête plus dessus : `intro` bascule tout de suite en
 * `jeu`, `score` ne se voit plus que pour le DERNIER mini-jeu (qui bascule dans le récap) ou une production reprise.
 * Les notes se lisent « 14 / 100 » avec un mot (Raté, Correct, Bien, Parfait : `atelier/core/note.ts`).
 *
 * ## Qui tient le pattern, et qui tient le score
 *
 * | | Bac à sable | Compte |
 * |---|---|---|
 * | Le pattern | tiré ici, du RNG du moteur sur `run.seed` | **`GET /atelier-pattern`**, une fois par production |
 * | Le score de l'étape | calculé ici, et il fait foi | calculé ici pour l'écran ; **recalculé** par le serveur depuis la timeline |
 * | Ce que `produce_video` emporte | les cinq scores | les cinq **timelines** |
 *
 * Côté compte, `run.seed` vaut `""` : le serveur ne publie jamais la graine (§5.4), sinon le
 * client saurait prédire tous les tirages. C'est toute la raison d'être de cet endpoint — et la
 * raison pour laquelle le mode compte savait tout faire **sauf** produire une vidéo.
 *
 * ## Le mode jalon (§9.3, PLAN §4.13)
 *
 * Quand le brouillon porte un `jalonId`, la **séquence ne change pas d'un pixel** — cinq
 * étapes, mêmes mini-jeux, même partition serveur, même one-shot — et **seule la destination
 * change** : `video_jalon` au lieu de `produce_video`. C'est ce qui rend un jalon jouable sans
 * dupliquer une ligne du pipeline, et c'est aussi ce que le contrat exige : `video_jalon` porte
 * des `AtelierScores`, et `executionJalon` fait la moyenne des étapes **jouées** — un envoi à
 * scores vides noterait 0 et ferait échouer le jalon, ce qui est pire que pas de bouton.
 *
 * Deux différences, et elles sont toutes deux des **absences** : un jalon n'a pas de sujet
 * (donc pas d'`alignementSujet`, comme le serveur qui n'en passe aucun au scoreur de
 * `milestone_video`) et pas de production value (l'action n'a pas de champ pour la porter).
 */

import { useEffect, useRef, useState, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";

import {
  CAP_TOLERANCE,
  H_JOUR,
  H_VIDEO_JALON,
  JALON_EFFET_RATE,
  SEUIL_JALON_REUSSI,
  estMonetise,
  exigenceAtelier,
  marcheOuvert,
  prochaineProd,
  scoreEffectifAtelier,
  type AtelierStage,
  type GameState,
  type OffreFreelance,
  type VideoRecord,
} from "@maxime/shared";

import { patternsAtelier, timelineVide } from "../../api/atelier";
import { vueMarche, type VueMarche } from "../../api/marche";
import { CouperDansLeRythme } from "../../atelier/CouperDansLeRythme/CouperDansLeRythme";
import { CurseurStub } from "../../atelier/CurseurStub";
import { LaDecoupe } from "../../atelier/LaDecoupe/LaDecoupe";
import { PriseParfaite } from "../../atelier/PriseParfaite/PriseParfaite";
import { PutaclicOMetre } from "../../atelier/PutaclicOMetre/PutaclicOMetre";
import { ScriptQuiTombe } from "../../atelier/ScriptQuiTombe/ScriptQuiTombe";
import { DessousEnPlus } from "../../atelier/core/DessousMiniJeu";
import { libelleAssist } from "../../atelier/core/libelles";
import { MotDeNote, NoteSur100 } from "../../atelier/core/NoteSur100";
import { SEUIL_NOTE_CORRECT, SEUIL_NOTE_PARFAIT, echelleDesNotes } from "../../atelier/core/note";
import { REGLES_COURTES } from "../../atelier/core/textes";
import type { ProprietesMiniJeu } from "../../atelier/core/types";
import { contenu } from "../../game/content";
import { LECONS_MINIJEU } from "../../game/decouverte";
import { messageErreur } from "../../game/erreurs";
import { ETAPES, jourSimule, nombre } from "../../game/format";
import { assists, modificateurs } from "../../game/moteur";
import {
  etapesDuFormat,
  scoresPourMoteur,
  soumissionAtelier,
  useAtelierStore,
} from "../../stores/atelierStore";
import { useDecouverteStore } from "../../stores/decouverteStore";
import { useRunStore } from "../../stores/runStore";
import { dialog, useUIStore } from "../../stores/uiStore";

import { BoiteDialogue, type Interlocuteur } from "../../components/BoiteDialogue";
import { DessousBloc, DessousLignes, LesDessous } from "../../components/LesDessous";
import { useSfx } from "../../components/SoundProvider";

import { AccessibiliteAtelier } from "./AccessibiliteAtelier";
import { BlocDelegation, BlocProduction, BlocServeur } from "./DessousAtelier";
import { PiedAtelier } from "./PiedAtelier";
import { CadreAtelier } from "./CadreAtelier";
import { Publication } from "./Publication";
import { ResultatJalon, faitJalon, type FaitJalon } from "./ResultatJalon";

/**
 * Les cinq mini-jeux — un **verbe par étape**, comme l'exige l'avenant **A-3** : ranger (⟦C52 ·
 * A-170⟧ : des phrases qui tombent, à pousser dans la bonne colonne), jouer (⟦C53 · A-170 §2⟧ :
 * « La prise parfaite », ex-cadrer), rythmer, doser, parier. Le registre reste ouvert : ce qui n'y
 * serait pas retomberait sur le curseur stub, et l'Atelier continuerait de tourner.
 */
const MINI_JEUX: Partial<Record<AtelierStage, ComponentType<ProprietesMiniJeu>>> = {
  ecriture: ScriptQuiTombe,
  tournage: PriseParfaite,
  montage: CouperDansLeRythme,
  miniature: PutaclicOMetre,
  promotion: LaDecoupe,
};

/**
 * ⟦C23 §4⟧ L'hôte, Matt, dans l'écran d'intro d'un mini-jeu vu pour la première fois. Même
 * constante que `Studio.tsx`/`NouvelleRun.tsx` (non exportée là-bas non plus, retapée à
 * l'identique, jamais un import cross-écran pour une poignée de deux champs).
 */
const HOTE: Interlocuteur = { nom: "Matt", portrait: "/avatar/portrait-mathieu.png" };

/** Le mini-jeu et son id de contenu, pour lire les assists (SPEC-CONTENU §2.4). */
const MINIJEU_DE_LETAPE = {
  ecriture: "fil_rouge",
  tournage: "bonne_prise",
  montage: "couper_rythme",
  miniature: "putaclic_o_metre",
  promotion: "la_decoupe",
} as const;

export function Atelier() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);
  const suspendreMoments = useUIStore((s) => s.suspendreMoments);
  const naviguer = useNavigate();
  const sfx = useSfx();

  const brouillon = useAtelierStore((s) => s.brouillon);
  const index = useAtelierStore((s) => s.index);
  const phase = useAtelierStore((s) => s.phase);
  const scores = useAtelierStore((s) => s.scores);
  const timelines = useAtelierStore((s) => s.timelines);
  const patterns = useAtelierStore((s) => s.patterns);
  const etatPatterns = useAtelierStore((s) => s.etatPatterns);
  const erreurPatterns = useAtelierStore((s) => s.erreurPatterns);
  const setPhase = useAtelierStore((s) => s.setPhase);
  const poserScore = useAtelierStore((s) => s.poserScore);
  const poserPatterns = useAtelierStore((s) => s.poserPatterns);
  const rafraichirPromotion = useAtelierStore((s) => s.rafraichirPromotion);
  const etapeSuivante = useAtelierStore((s) => s.etapeSuivante);
  const embaucher = useAtelierStore((s) => s.embaucher);
  const reset = useAtelierStore((s) => s.reset);

  const [resultat, setResultat] = useState<VideoRecord | null>(null);
  /**
   * Le résultat d'une vidéo **jalon**. Un champ à part parce qu'un jalon ne produit aucun
   * `VideoRecord` (le % *est* l'effet) : il n'y a rien à mettre dans `resultat`.
   */
  const [resultatJalon, setResultatJalon] = useState<FaitJalon | null>(null);
  /**
   * Incrémenté par « Réessayer » — et par un changement de tolérance entre deux étapes (§7.3) :
   * c'est lui, et lui seul, qui redemande les patterns.
   */
  const [essai, setEssai] = useState(0);
  /** ⟦SPEC-UI §7.3⟧ Le dialogue d'accessibilité, ouvert depuis la barre de pied. */
  const [accessibiliteOuverte, setAccessibiliteOuverte] = useState(false);
  const toleranceReglage = useUIStore((s) => s.settings.tolerance);
  /**
   * ⟦A-83 · SPEC-UI §7.4⟧ `prefers-reduced-motion` — le mode posé. Lu du media query (jamais
   * stocké), demandé au serveur avec les patterns (`reduced_motion=1`) et redit à la soumission :
   * le pattern joué et le pattern noté sont les mêmes, des deux côtés.
   */
  const reducedMotion = useUIStore((s) => s.reducedMotion);

  const scoreDeDev = lireScoreDeDev();

  // ── ⟦A-9 · A-129 C11⟧ Le mode découverte, côté Atelier ────────────────────────────────
  // Depuis C11, ces leçons ne passent PLUS par le voile bloquant : leur contenu est fusionné
  // dans l'écran d'intro du mini-jeu lui-même (plus bas), la première fois seulement.
  // `marquerVue` les consigne au clic sur Jouer — plus de `demander`/`courante`, ce mini-jeu ne
  // bloque jamais le montage du jeu qui, lui, n'attend que le clic du joueur.
  const decouverteActive = useUIStore((s) => s.settings.decouverte);
  const marquerLeconVue = useDecouverteStore((s) => s.marquerVue);
  const leconsVues = useDecouverteStore((s) => s.vues);

  // ⟦A-4 E · M3.30⟧ **Les étapes embauchées ne se jouent pas** : le joueur a payé pour ne pas
  // tenir les ciseaux sur celles-là. La séquence JOUÉE est le pipeline moins les embauches ;
  // la soumission, elle, porte toujours les cinq étapes (déléguées comprises, avec QUI).
  const embauchesProd = brouillon?.embauches ?? {};
  const etapesJouees = brouillon
    ? etapesDuFormat(brouillon.format).filter((s) => embauchesProd[s] === undefined)
    : [];

  // L'étape courante, calculée avant les retours anticipés : les hooks ne se sautent pas.
  const stageCourant = brouillon ? (etapesJouees[index] ?? null) : null;
  const leconDuStage = stageCourant ? LECONS_MINIJEU[stageCourant] : null;
  // ⟦A-129 · C11 · C23 §4⟧ Première fois que CE mini-jeu se joue, mode découverte actif : Matt dit sa
  // réplique. Les fois suivantes, la phrase de règle d'avant.
  // ⟦C62 · lot atelier · friction 3⟧ La réplique n'a plus d'écran à elle : elle est posée sur l'écran de
  // consigne du mini-jeu, à la place de sa phrase de règle (`accueil`, plus bas), UNE réplique, la courte
  // (`LECONS_MINIJEU[stage].court`, 12 mots au plus ; les deux répliques d'origine, le détail et « à retenir »
  // se relisent derrière « Les dessous » du mini-jeu).
  const premiereFoisCeMinijeu =
    decouverteActive && leconDuStage !== null && !leconsVues.includes(leconDuStage.id);
  // ⟦C23 §4⟧ L'étape vue au dernier rendu : un `useState` ajusté PENDANT le rendu (même patron que
  // `useMattChambre`, jamais un `ref` muté au rendu) pour refermer le rappel de règle quand l'étape change.
  const [dernierStageVu, setDernierStageVu] = useState<AtelierStage | null>(null);
  if (stageCourant !== dernierStageVu) setDernierStageVu(stageCourant);
  // ⟦C23 §4⟧ Le rappel de la règle en réplique, ouvert depuis le bouton « ? » du pied
  // (`PiedAtelier`), jamais plus d'un mini-jeu de retard : refermé dès que l'étape change.
  const [rappelRegleOuvert, setRappelRegleOuvert] = useState(false);
  if (stageCourant !== dernierStageVu && rappelRegleOuvert) setRappelRegleOuvert(false);

  // ── La couture : aller chercher les patterns du tour ───────────────────────────────────
  const runId = run?.runId ?? null;
  /** `true` quand la simulation est autoritative : c'est elle qui tient la graine et le score. */
  const surServeur = run !== null && !run.bacASable;
  /** ⟦C23 §4⟧ Hissé plus haut (était défini juste avant son seul usage) : la nouvelle logique
   *  d'auto-saut de l'intro (effet ci-dessous) en a besoin AVANT le premier retour anticipé du
   *  composant (`if (!run || !brouillon) return null`, règle des hooks : call order stable). */
  const patternsIndispensables = surServeur && etatPatterns !== "prets";
  // ⟦C23 §4⟧ Retour de Matt : « 3 pop-ups d'explications pour chaque nouveau mini-jeu, beaucoup
  // trop long », un short en enchaîne trois d'affilée la première fois. Les fois SUIVANTES,
  // plus d'écran d'intro du tout : dès que la partition est prête, on saute directement en phase
  // `jeu`. La règle en une phrase reste lisible en permanence dans `PiedAtelier`, plus bas.
  // ⟦C62 · lot atelier · friction 3⟧ **Et la première fois aussi.** L'écran d'intro de Matt (avec son « Jouer »)
  // devant l'écran de consigne du mini-jeu (avec le sien) : deux clics pour lancer UN jeu, et un « Jouer » qui
  // semble ne rien démarrer. Il n'y a plus qu'un écran, celui du mini-jeu : la réplique de Matt y prend la place
  // de sa phrase de règle (`accueil`, plus bas), un seul « Jouer ».
  useEffect(() => {
    if (patternsIndispensables) return;
    if (phase !== "intro") return;
    setPhase("jeu");
  }, [patternsIndispensables, phase, setPhase]);

  /**
   * ⚠️ **Le garde-fou est une ref, pas `etatPatterns`.** Poser « chargement » dans l'effet et
   * lire `etatPatterns` en dépendance, c'est se rejouer soi-même : React exécute alors le
   * nettoyage de la passe précédente — donc `vivant = false` — et la réponse qui arrive ensuite
   * est jetée. L'écran resterait sur « on récupère la partition » pour toujours.
   */
  const demandeFaite = useRef<string | null>(null);
  useEffect(() => {
    if (!brouillon || !runId || !surServeur) return;
    const cle = `${runId}:${brouillon.format}:${brouillon.jalonId ?? ""}:${brouillon.subjectId}:${essai}:${reducedMotion ? "pose" : ""}`;
    if (demandeFaite.current === cle) return;
    demandeFaite.current = cle;
    poserPatterns(null, "chargement");
    // ⚠️ Un jalon a `subjectId === ""`, donc **aucun `subject_id` ne part** (`patternsAtelier`
    // ne pose le paramètre que s'il est renseigné). C'est exactement ce qu'il faut : le serveur
    // ne passe aucun alignement au scoreur de `milestone_video`, et un pattern demandé avec un
    // sujet ne serait pas celui qu'il régénérera pour noter les gestes.
    patternsAtelier(runId, {
      format: brouillon.format,
      subject_id: brouillon.subjectId,
      reduced_motion: reducedMotion,
    })
      .then((p) => poserPatterns(p, "prets"))
      .catch((e: unknown) => poserPatterns(null, "erreur", messageErreur(e)));
  }, [brouillon, runId, surServeur, essai, reducedMotion, poserPatterns]);

  /**
   * ⟦A-3⟧ Les marqueurs de La Découpe s'achètent avec les scores des étapes précédentes — que
   * l'appel initial, tiré avant le premier mini-jeu, ne pouvait pas connaître. On redemande donc
   * le pattern juste avant l'étape, et **seulement** son bloc `promotion`.
   *
   * C'est sans danger : `scores_precedents` n'entre dans aucun tirage (il est lu après tous les
   * `rng` de `promotion()`) et **aucun barème n'en dépend** — le scoreur régénère d'ailleurs le
   * pattern avec une liste vide. Les zones chaudes rendues sont donc les mêmes, aux marqueurs
   * près. Si l'appel rate, on garde le pattern initial : un indice manquant n'est pas une règle
   * cassée.
   *
   * ⟦C62 · lot atelier · friction 3⟧ **Une seule fois par entrée dans l'étape, plus « à l'intro ».** Cet effet ne
   * tournait que pendant la phase `intro` et jetait sa réponse (`vivant = false`) dès que la phase changeait :
   * avec l'intro sautée (la règle des fois suivantes, désormais celle de toutes les fois), la réponse arrivait
   * toujours après le changement de phase, et les marqueurs ne se posaient jamais. Une clé gardée dans une ref
   * (la run, l'étape, les scores d'avant, l'essai) fait la même chose sans dépendre de la phase ; elle est
   * invalidée quand l'écran se démonte, pour qu'une réponse tardive ne se pose pas sur la production d'après.
   */
  const promoRafraichie = useRef<string | null>(null);
  useEffect(
    () => () => {
      promoRafraichie.current = "demonte";
    },
    [],
  );
  useEffect(() => {
    if (!brouillon || !runId || !surServeur) return;
    if (stageCourant !== "promotion" || phase === "score" || etatPatterns !== "prets") return;
    // Les étapes JOUÉES avant celle-ci — une étape embauchée n'a pas de score de geste.
    const precedents = etapesJouees
      .slice(0, index)
      .map((s) => scores[s])
      .filter((s): s is number => typeof s === "number");
    if (precedents.length === 0) return;
    const cle = `${runId}:${index}:${precedents.join(",")}:${essai}`;
    if (promoRafraichie.current === cle) return;
    promoRafraichie.current = cle;
    patternsAtelier(runId, {
      format: brouillon.format,
      subject_id: brouillon.subjectId,
      scores_precedents: precedents,
      reduced_motion: reducedMotion,
    })
      .then((p) => {
        if (promoRafraichie.current === cle) rafraichirPromotion(p);
      })
      .catch(() => {
        /* les marqueurs sont un indice, pas une règle : on s'en passe en silence */
      });
    // `scores` volontairement hors dépendances : il change à chaque étape jouée, et on ne veut
    // qu'un seul appel, celui de l'entrée dans la Promotion (la clé le garantit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brouillon, runId, surServeur, stageCourant, phase, etatPatterns, index, essai, rafraichirPromotion]);

  /**
   * ⟦A-83 · SPEC-UI §3.3/§5.6⟧ **Le marché, pour le bouton « Déléguer » de l'intro.** La même
   * lecture que l'écran du marché et que le devis de `NouvelleVideo` (`vueMarche` : local en bac
   * à sable, `GET /runs/:id/marche` en compte — la graine ne sort jamais du serveur). On ne la
   * demande que si l'arbre a ouvert le marché (`marcheOuvert`, une lecture de modificateurs) :
   * sans nœud, pas d'appel, pas de bouton — ⟦A-5⟧ « ce qui n'est pas déverrouillé est absent ».
   * Une seule lecture par production : l'offre est une forme close de la fenêtre de 7 jours, et
   * l'horloge ne bouge pas pendant l'Atelier.
   */
  const [marche, setMarche] = useState<VueMarche | null>(null);
  // ⟦A-78⟧ La porte est le Grand Saut : `marcheOuvert` lit l'état, plus seulement l'arbre.
  const marcheParLArbre = run ? marcheOuvert(run.state, modificateurs(run.state)) : false;
  useEffect(() => {
    if (!run || !brouillon || !marcheParLArbre) return;
    let vivant = true;
    vueMarche(run)
      .then((v) => {
        if (vivant) setMarche(v);
      })
      .catch(() => {
        /* pas d'offre, pas de bouton : le marché reste joignable depuis la chambre */
      });
    return () => {
      vivant = false;
    };
    // `run` volontairement hors dépendances : la lecture vaut pour toute la production.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, brouillon, marcheParLArbre]);

  // Tant que l'Atelier tient l'écran (jeu ET résultat), les annonces de déverrouillage
  // attendent : on ne coupe pas une courbe qui se trace par une modale ⟦A-5⟧.
  useEffect(() => {
    suspendreMoments(true);
    return () => suspendreMoments(false);
  }, [suspendreMoments]);

  useEffect(() => {
    if (!brouillon) naviguer("/nouvelle-video", { replace: true });
  }, [brouillon, naviguer]);

  if (!run || !brouillon) return null;
  const state = run.state;
  const c = contenu();
  const sujet = c.parSujet.get(brouillon.subjectId);
  const etapes = etapesDuFormat(brouillon.format);
  const exigence = exigenceAtelier(state);

  // ── Le mode jalon (§9.3) : une destination, pas une séquence différente ──────────────────
  const jalonId = brouillon.jalonId ?? null;
  const jalon = jalonId === null ? null : (c.milestones.find((j) => j.id === jalonId) ?? null);
  /**
   * Ce que l'en-tête et le récap nomment : le jalon, ou le sujet de la vidéo. `jalonId` sert de
   * dernier recours — un jalon absent du contenu (versions désaccordées) doit s'afficher par son
   * id plutôt que par un « » vide, et c'est le moteur qui le refusera à la publication.
   */
  const titreProjet = jalon?.nom ?? sujet?.nom ?? jalonId ?? brouillon.subjectId;

  /**
   * ⟦SPEC-UI §7.3⟧ **La tolérance telle qu'elle s'applique à CETTE production** — pas telle
   * qu'une case la montre. Côté compte, c'est le pattern servi qui fait foi (`toleranceApplied`,
   * posé par le serveur depuis `flags.accessibility_tolerance`) : le miroir d'état peut être en
   * retard d'un `PATCH`, le pattern jamais. En bac à sable, le réglage d'interface est la seule
   * source — c'est lui que le générateur local lit.
   */
  const toleranceActive = surServeur
    ? (patterns?.patterns.ecriture.toleranceApplied ?? state.flags.accessibility_tolerance === true)
    : toleranceReglage;
  /** §7.4 ✎ : le mode posé plafonne à 85 comme la tolérance — l'écran de score doit le dire. */
  const plafondAccessibilite: "tolérance" | "mode posé" | null = toleranceActive
    ? "tolérance"
    : reducedMotion
      ? "mode posé"
      : null;
  /** L'exigence du tour : celle du pattern servi, sinon celle du moteur. */
  const niveauExigence = patterns ? patterns.difficulty_tier : exigence.k;
  /**
   * ⟦SPEC-UI §7.3⟧ **En bac à sable, le réglage entre dans l'état que les mini-jeux lisent.**
   * Les générateurs locaux demandent `exigenceAtelier(state)`, qui lit
   * `flags.accessibility_tolerance` — or le bac à sable n'a pas de serveur pour poser ce
   * drapeau : la case « tolérance » ne changeait ni une fenêtre ni un plafond hors compte. On
   * tend donc aux mini-jeux un état qui porte le réglage ; le moteur, lui, reçoit les scores
   * tels que le mini-jeu les a plafonnés. Côté compte, l'état vient du serveur et porte déjà
   * le drapeau : identité.
   */
  const stateAtelier: GameState = surServeur
    ? state
    : { ...state, flags: { ...state.flags, accessibility_tolerance: toleranceReglage } };

  const pied = (
    <PiedAtelier
      etapes={etapes}
      scores={scores}
      deleguees={embauchesProd}
      exigence={niveauExigence}
      toleranceActive={toleranceActive}
      onAccessibilite={() => setAccessibiliteOuverte(true)}
      // ⟦C23 §4⟧ La règle en une phrase de l'étape en cours, permanente dans le pied (`null`
      // hors étape (écran de résultat/publication, `stageCourant` déjà `null` là-bas).
      regle={stageCourant ? REGLES_COURTES[stageCourant] : null}
      // ⟦C57 · lot 8⟧ Le « ? » fait redire à Matt sa réplique courte de l'étape.
      rappel={leconDuStage ? leconDuStage.court : null}
      rappelOuvert={rappelRegleOuvert}
      onOuvrirRappel={() => setRappelRegleOuvert(true)}
      onFermerRappel={() => setRappelRegleOuvert(false)}
    />
  );
  const accessibilite = (
    <AccessibiliteAtelier
      ouvert={accessibiliteOuverte}
      enPleinJeu={phase === "jeu" && index < etapesJouees.length}
      surServeur={surServeur}
      onFermer={() => setAccessibiliteOuverte(false)}
      onToleranceChangee={() => {
        // Même tour ⇒ mêmes tirages ; seules les fenêtres et les chronos bougent. On redemande
        // la partition pour que l'écran joue celle que le serveur notera.
        if (surServeur) setEssai((n) => n + 1);
      }}
    />
  );
  /**
   * ⚠️ **Zéro pour un jalon, et ce n'est pas un défaut de valeur.** Le serveur n'envoie aucun
   * `alignementSujet` au scoreur pour `milestone_video` (`routes/actions.ts`) : lui en donner un
   * ici resserrerait la zone du putaclic-o-mètre côté client sur une partition que le serveur
   * régénérerait sans lui — le geste joué et le geste noté ne seraient plus les mêmes.
   */
  const alignementSujet = jalon ? 0 : (sujet?.alignement ?? 0);

  /**
   * ⟦A-33 · A-47⟧ **Ce que la chaîne retient VRAIMENT de ce geste** — et c'est la seule chose
   * que l'écran a le droit d'annoncer comme telle.
   *
   * On appelle **la fonction du moteur**, celle-là même que `scoresEffectifs` applique à la
   * publication (`publish.ts`). ⟦A-10⟧ À `K_SURPASSEMENT = 0` elle rend `min(geste, plafond)` au
   * caractère près : cet écran ne bouge pas d'un pixel tant que la constante n'a pas bougé.
   *
   * ⟦A-129 · C11⟧ Le plafond BRUT (jusqu'où ma chaîne exploite pleinement mon geste) et son
   * maximum théorique de compression ne sont plus affichés nulle part dans l'Atelier : cette
   * pédagogie (l'exigence E0 et ses deux bornes de rétention) vit désormais dans le
   * magazine-guide (`content/guide.json`, dossier « Ton geste, et ce que ta chaîne en
   * retient »). Seule reste la valeur retenue, `retenuDe`, affichée sur l'écran de score.
   */
  const retenuDe = (stage: AtelierStage, score: number) =>
    scoreEffectifAtelier(score, state, stage, c, {
      production_value: brouillon?.productionValue ?? 0,
    });

  async function publier() {
    if (!brouillon) return;

    // ── La couture, au moment où elle compte ────────────────────────────────────────────
    let contexte: { atelier: NonNullable<ReturnType<typeof soumissionAtelier>> } | undefined;
    if (surServeur) {
      const soumission = soumissionAtelier(brouillon.format, timelines, embauchesProd, reducedMotion);
      if (!soumission) {
        // ⟦C57 · lot 8⟧ Une phrase : le détail (« le serveur note l'Atelier lui-même… ») est derrière
        // « Les dessous » de « Prêt à publier » (`BlocServeur`).
        await dialog.info({
          title: "Il manque des gestes",
          body: "Une étape n'a pas laissé de trace : rejoue la production.",
        });
        return;
      }
      // Le pattern a été tiré pour UN tour. Si le tour a bougé entre-temps (un autre onglet, un
      // événement résolu), le serveur régénérerait un autre pattern et noterait des gestes qui
      // ne lui correspondent plus. On le dit plutôt que de publier une note absurde.
      if (patterns && patterns.turn !== state.turn) {
        await dialog.info({
          title: "Production en retard d'un tour",
          body: `Jouée au tour ${patterns.turn}, tu es au tour ${state.turn} : reprends la production.`,
        });
        return;
      }
      contexte = { atelier: soumission };
    }

    sfx("publish_whoosh");
    const scoresMoteur = scoresPourMoteur(brouillon.format, scores, embauchesProd);
    // `0` ⇒ champ absent : l'état neutre d'⟦A-11 §3⟧, celui où aucune ancre ne bouge.
    const pv = brouillon.productionValue > 0 ? brouillon.productionValue : undefined;
    const resultatAction = await act(
      // §9.3 — **la seule bifurcation du pipeline** : mêmes scores, mêmes timelines, autre
      // action. `video_jalon` ne porte ni sujet, ni evergreen, ni production value : l'union du
      // moteur n'a pas de champ pour eux (`types.ts`), et le contenu du jalon décide du reste.
      jalonId !== null
        ? { type: "video_jalon", jalon_id: jalonId, scores: scoresMoteur }
        : brouillon.format === "short"
          ? {
              type: "produce_video",
              format: "short",
              subject_id: brouillon.subjectId,
              scores: scoresMoteur,
              production_value: pv,
            }
          : {
              type: "produce_video",
              format: "longue",
              subject_id: brouillon.subjectId,
              evergreen_intent: brouillon.evergreenIntent,
              extra_card_subjects: brouillon.extraCardSubjects,
              scores: scoresMoteur,
              production_value: pv,
            },
      contexte,
    );
    if (!resultatAction) return; // le moteur a refusé : le toast l'a dit, on reste ici

    if (jalonId !== null) {
      // Un jalon n'a pas de `VideoRecord` : son résultat est l'événement `jalon_done`, et il
      // arrive par le même chemin dans les deux modes. S'il manquait, on rendrait la main
      // plutôt que d'inventer un écran — le journal, lui, porte déjà la ligne du moteur.
      const fait = faitJalon(resultatAction.events);
      if (fait) setResultatJalon(fait);
      else quitterResultat();
      return;
    }

    const video =
      resultatAction.run.state.videos.find((v) => v.id === resultatAction.videoPubliee) ?? null;
    setResultat(video);
  }

  function quitterResultat() {
    reset();
    setResultat(null);
    setResultatJalon(null);
    naviguer("/", { replace: true });
  }

  /**
   * ⟦A-83 · SPEC-UI §3.3/§5.6⟧ **« Déléguer », depuis l'intro de l'étape.** Le bouton propose
   * l'offre du jour pour CETTE étape — celles que le marché affiche, aux prix que le marché
   * affiche — et le prix proposé est le prix débité : même `OffreFreelance.prix`
   * (`prixEffectif`, `MULT_PRIX_NOTORIETE` compris) que `offreRetenue` relira à la publication.
   *
   * Dialogue maison (§7.1) : une option par personne disponible, avec son rendu attendu et son
   * prix ; on n'y met que celles qu'on peut payer — le moteur refuserait `argent` après les
   * mini-jeux, la pire façon d'apprendre qu'on n'a pas la caisse. Le choix va dans
   * `brouillon.embauches` par `embaucher` : c'est le chemin du plan du marché, pas un second.
   */
  async function deleguer(stage: AtelierStage, offres: readonly OffreFreelance[]) {
    if (!brouillon) return;
    // Ce qui est déjà engagé sur cette production : les étapes confiées avant, et la production
    // value. Le moteur vérifie le total à la publication ; on le lit pareil, avant.
    const engage = etapes.reduce((t, s) => {
      const id = embauchesProd[s];
      if (id === undefined) return t;
      return t + (marche?.etapes[s]?.find((o) => o.id === id)?.prix ?? 0);
    }, brouillon.productionValue);
    const payables = offres.filter((o) => state.money >= engage + o.prix);
    if (payables.length === 0) {
      // ⟦C57 · lot 8⟧ Des nombres : la caisse, et ce qu'il faudrait. « Tu reprends les ciseaux » et le détail
      // des engagés sont derrière « Les dessous » de l'intro (`BlocDelegation`).
      await dialog.info({
        title: "Pas de quoi payer",
        body: `Caisse : ${nombre(state.money)} ₵ · il en faut ${nombre(engage + (offres[0]?.prix ?? 0))} ₵.`,
      });
      return;
    }
    const choix = await dialog.choose({
      title: `Confier ${ETAPES[stage].nom.toLowerCase()} à quelqu'un ?`,
      // ⟦C57 · lot 8⟧ Une ligne de nombres : la caisse, ce qui est déjà engagé. La phrase d'avant (la
      // livraison, le prix prélevé à la publication) est derrière « Les dessous » de l'intro (`BlocDelegation`).
      body: `Caisse : ${nombre(state.money)} ₵${engage > 0 ? ` · engagés : ${nombre(engage)} ₵` : ""} · payé à la publication.`,
      options: payables.map((o) => ({
        id: o.id,
        label: `${o.nom} · ${nombre(o.prix)} ₵`,
        hint:
          `rendu ${o.cote} ± ${o.variance}` +
          (o.pepite ? " · 🌱 pépite" : "") +
          (o.fidelise ? " · 🤝 fidélisée" : "") +
          (o.embauches > 0 ? ` · ${o.embauches} fois ensemble` : ""),
      })),
      annulable: true,
    });
    if (choix === null) return;
    embaucher(stage, choix);
  }

  async function abandonner() {
    const ok = await dialog.confirm({
      title: "Jeter le projet ?",
      // ⟦C57 · lot 8⟧ Une phrase ; « la production ne coûte qu'à la publication » est dans `BlocProduction`.
      body: "Rien n'est dépensé, mais ce travail est perdu.",
      confirmLabel: "Jeter",
      cancelLabel: "Continuer",
      danger: true,
    });
    if (!ok) return;
    reset();
    naviguer("/", { replace: true });
  }

  // ───────────────────────── Résultat d'un jalon (§9.3) ─────────────────────────
  if (resultatJalon) {
    return (
      // ⟦C57 · lot 8⟧ Le nom du jalon est le titre du résultat : pas de doublon dans le bandeau.
      <CadreAtelier>
        <ResultatJalon fait={resultatJalon} onFermer={quitterResultat} />
      </CadreAtelier>
    );
  }

  // ───────────────────────── Écran de résultat ⟦A-8⟧ ─────────────────────────
  if (resultat) {
    return (
      // ⟦C57 · lot 8⟧ Le sujet est écrit une fois, dans le corps du résultat : pas de doublon dans le bandeau.
      <CadreAtelier>
        <Publication
          video={resultat}
          sujet={sujet?.nom ?? resultat.subject_id}
          state={state}
          jourDePublication={jourSimule(resultat.published_at_h)}
          monetise={estMonetise(state)}
          onFermer={quitterResultat}
        />
      </CadreAtelier>
    );
  }

  // ───────────────────────── Récap final ─────────────────────────
  // ⟦A-129 · C11⟧ **Fusion score + récap, pour le DERNIER mini-jeu de la séquence.** Avant, le
  // joueur voyait l'écran de score de la dernière étape, cliquait « Voir le récap », et
  // atterrissait sur un second écran qui redisait les mêmes scores. Les deux écrans n'en font
  // plus qu'un : dès que le score de la dernière étape est posé (`phase === "score"`, plus
  // besoin d'attendre `etapeSuivante()`), on rend directement le récap — tous les scores sont
  // déjà dans `scores`, rien n'attend l'incrément d'`index`.
  const dernierScorePosePasseDirectementAuRecap =
    phase === "score" && index === etapesJouees.length - 1;
  if (index >= etapesJouees.length || dernierScorePosePasseDirectementAuRecap) {
    const notes = etapes
      .map((stage) => scores[stage])
      .filter((s): s is number => typeof s === "number");
    const moyenne = notes.length === 0 ? null : notes.reduce((t, v) => t + v, 0) / notes.length;

    /**
     * §9.3 — **le score d'exécution d'un jalon est la moyenne simple des cinq étapes
     * plafonnées** (`executionJalon` après `scoresEffectifs`), pas une moyenne pondérée comme
     * une publication ordinaire. On peut donc dire au joueur, avant qu'il clique, s'il est
     * au-dessus de `SEUIL_JALON_REUSSI` — et c'est la seule chose qui rend le risque lisible.
     *
     * `null` sur une étape (La Découpe, notée par le serveur) devient `0` chez le moteur : la
     * prévision reste vraie côté bac à sable, et se dit **incomplète** en mode compte.
     */
    const moyenneJalon =
      etapes.reduce((t, stage) => t + retenuDe(stage, scores[stage] ?? 0), 0) / etapes.length;
    const previsionIncomplete = etapes.some((stage) => typeof scores[stage] !== "number");
    /** ⟦A-9 · A-32⟧ Au moins une étape jouée dépasse ce que la chaîne en retient. */
    const gestePlusFortQueLaChaine = etapes.some(
      (stage) => retenuDe(stage, scores[stage] ?? 0) < (scores[stage] ?? 0),
    );

    return (
      <CadreAtelier sujet={titreProjet}>
        <EnteteAtelier
          etapes={etapes}
          index={index}
          sujet={titreProjet}
          onAbandon={() => void abandonner()}
          deleguees={embauchesProd}
        />
        <section className="carte scotche">
          {/* ⟦C57 · lot 8 · A-174⟧ **Une ligne par étape, deux nombres, un verdict, un bouton.** Titre de 3
              mots ; chaque étape garde son score en gros ; la phrase de la chaîne (« retient N ») reste en
              clair, A-47 l'exige ; tout le reste (le risque du jalon, ce que la chaîne retient, la
              livraison d'une étape confiée, le calcul du navigateur) est derrière « Les dessous ». */}
          <h2 className="bloc-titre">{jalon ? "🏆 Jalon prêt à publier" : "Prêt à publier"}</h2>
          {/* ⟦A-32⟧ **Le geste appartient au joueur, la conversion appartient à la chaîne.**
              Mot de Mathieu après un playtest : *« la personne, quand on joue, elle a un score
              qui est réduit vers le bas au mini-jeu. Ce n'est pas le score qui doit être réduit
              […] Tu imagines comment c'est frustrant : le mec fait 100 points et tu lui dis
              "bah non, en fait ça fait 70". »*

              Cette liste affichait « 9̶8̶ → 70 ». Le chiffre barré est ce que l'œil lit en
              premier, et il se lit comme une note corrigée en rouge sur une copie. Le score joué
              n'est donc PLUS jamais raturé : il reste entier, en gros, à sa place de
              performance. Ce que la chaîne en retient se dit à côté, en clair, comme une marge
              de progression.

              ⚠️ **On ne ment pas dans l'autre sens** : la valeur retenue reste écrite noir sur
              blanc, parce que c'est elle que le moteur utilise. On cesse de la présenter comme
              une correction, on ne la cache pas.

              ⟦A-47⟧ Et depuis que la compression est ouverte (`K_SURPASSEMENT = 0,60`), cette
              valeur n'est PLUS le plafond : elle monte avec le geste. Raison de plus pour la
              lire du moteur et jamais la recalculer ici.

              ⟦A-129 · C11⟧ Le mot « exploite » a quitté cet écran : la formule interne (E0,
              plafond, compression) vit dans le guide, pas ici — seule reste la valeur RETENUE,
              écrite en clair. */}
          <ul className="recap">
            {etapes.map((stage) => {
              // ⟦A-4 E · M3.30⟧ Une étape embauchée ne porte pas de geste : elle porte un NOM
              // et un PRIX — la livraison (cote ± variance) tombera à la publication.
              const embaucheId = embauchesProd[stage];
              if (embaucheId !== undefined) {
                const fiche = c.parFreelance.get(embaucheId);
                return (
                  <li key={stage}>
                    <span className="recap-etape">
                      <span className="recap-etape-nom">
                        {ETAPES[stage].icone} {ETAPES[stage].nom}
                      </span>
                      <span className="recap-conversion">
                        confiée à <strong>{fiche?.nom ?? embaucheId}</strong>
                      </span>
                    </span>
                    <strong className="score-joue">🧑‍🔧</strong>
                  </li>
                );
              }
              const brut = scores[stage];
              // ⟦A-47⟧ La valeur du MOTEUR, arrondie pour l'œil — plus jamais `min(brut, plafond)`.
              const retenu =
                brut === null || brut === undefined ? null : Math.round(retenuDe(stage, brut));
              const bride = brut !== null && brut !== undefined && retenu !== null && retenu < brut;
              return (
                <li key={stage}>
                  <span className="recap-etape">
                    <span className="recap-etape-nom">
                      {ETAPES[stage].icone} {ETAPES[stage].nom}
                    </span>
                    {/* ⟦A-129 · C11⟧ L'ancienne formule (« la chaîne exploite N aujourd'hui »)
                        a disparu du vocabulaire de cet écran — cette pédagogie vit dans le
                        guide — mais la valeur retenue reste écrite noir sur blanc, comme
                        l'exige ⟦A-47⟧ : ce n'est pas au joueur de deviner ce que le moteur va
                        vraiment utiliser. */}
                    {bride ? (
                      <span className="recap-conversion">
                        ta chaîne en retient <strong>{retenu}</strong>
                      </span>
                    ) : null}
                  </span>
                  {/* ⟦C62 · friction 7⟧ « 14 », sans échelle : désormais « 14 / 100 » et un mot (Raté, Correct, Bien,
                      Parfait ; les seuils sont dans les dessous). */}
                  <span className="recap-note">
                    <strong className={bride ? "score-joue bride" : "score-joue"}>
                      {brut === null || brut === undefined ? (
                        <span title="Noté par le serveur">?</span>
                      ) : (
                        <>
                          {brut}
                          <span className="note-sur"> / 100</span>
                        </>
                      )}
                    </strong>
                    {brut === null || brut === undefined ? null : <MotDeNote note={brut} petit />}
                  </span>
                </li>
              );
            })}
          </ul>
          {moyenne === null ? null : (
            <p className="ligne-info">
              Moyenne de tes gestes : <strong>{Math.round(moyenne)} / 100</strong> <MotDeNote note={moyenne} petit />
            </p>
          )}

          {/* §9.3 — Le risque, dit AVANT le clic : le score retenu, le seuil, une pastille. Tous les
              chiffres viennent du moteur ; le coût d'un échec et l'effet d'une réussite sont derrière
              « Les dessous ». */}
          {jalon ? (
            <p className={`ligne-info ${moyenneJalon >= SEUIL_JALON_REUSSI ? "" : "discret"}`}>
              {moyenneJalon >= SEUIL_JALON_REUSSI ? "✅" : "⚠"} Retenu{" "}
              <strong>{Math.round(moyenneJalon)}</strong>, il en faut{" "}
              <strong>{SEUIL_JALON_REUSSI}</strong>.
            </p>
          ) : null}
          {/* ⟦A-9 · A-32⟧ « Comment dire que c'est bien joué, mais pas encore assez bon ? » La phrase qui
              parle de la CHAÎNE, pas du joueur : elle reste au premier plan, en UNE ligne. La suite
              (« tes notes ne bougent pas… », la production value) est derrière « Les dessous ». */}
          {gestePlusFortQueLaChaine ? (
            <p className="ligne-info bandeau-geste">
              <strong>Ton geste est meilleur que ta chaîne.</strong>
            </p>
          ) : null}

          {/* ⟦A-5 proposition 7⟧ « Rendre visible la progression RÉELLE du joueur » : ses
              propres scores, pris de `atelier_history`. Zéro donnée nouvelle, zéro stat
              inventée — c'est exactement ce que l'avenant refuse de flatter. */}
          <Progression state={state} etapes={etapes} />

          <div className="jeu-actions">
            <button
              className="bouton primaire grand"
              type="button"
              disabled={pending}
              onClick={() => void publier()}
            >
              {jalon ? "🏆 Publier le jalon" : "🚀 Publier"}
            </button>
            <LesDessous titre={jalon ? `Prêt à publier « ${jalon.nom} »` : "Prêt à publier"}>
              {/* ⟦C62 · friction 7⟧ L'échelle des notes, dite une fois : les seuils sont ceux du jeu (30 : sous ce
                  score la vidéo est nulle ; 60 : la barre d'un jalon ; 90 : le bonus de viralité). */}
              <DessousBloc titre="Les notes, sur 100">
                <DessousLignes
                  lignes={echelleDesNotes().map((e) => ({
                    icone: "📏",
                    texte: (
                      <>
                        <strong>{e.mot}</strong> : {e.fourchette} ({e.sens})
                      </>
                    ),
                  }))}
                />
              </DessousBloc>
              {etapes.some((stage) => embauchesProd[stage] !== undefined) ? (
                <DessousBloc titre="Étapes confiées">
                  <DessousLignes
                    lignes={etapes
                      .filter((stage) => embauchesProd[stage] !== undefined)
                      .map((stage) => ({
                        icone: ETAPES[stage].icone,
                        texte: `${ETAPES[stage].nom} : confiée à ${c.parFreelance.get(embauchesProd[stage]!)?.nom ?? embauchesProd[stage]}, la livraison arrive à la publication.`,
                      }))}
                  />
                </DessousBloc>
              ) : null}
              {gestePlusFortQueLaChaine ? (
                <DessousBloc titre="Ton geste et ta chaîne">
                  <p className="dessous-texte">
                    <strong>Ton geste est meilleur que ta chaîne.</strong> Tes notes ne bougent pas ;
                    ta chaîne, elle, n'en retient qu'une partie aujourd'hui.
                    {brouillon.productionValue > 0
                      ? ` Ta production value a déjà relevé ce plafond pour cette vidéo.`
                      : ``}
                  </p>
                  <DessousLignes
                    lignes={etapes
                      .filter((stage) => embauchesProd[stage] === undefined)
                      .filter((stage) => typeof scores[stage] === "number" && retenuDe(stage, scores[stage] ?? 0) < (scores[stage] ?? 0))
                      .map((stage) => ({
                        icone: ETAPES[stage].icone,
                        texte: `${ETAPES[stage].nom} : ta chaîne en retient ${Math.round(retenuDe(stage, scores[stage] ?? 0))}, le reste t'attend.`,
                      }))}
                  />
                </DessousBloc>
              ) : null}
              {jalon ? (
                <DessousBloc titre="Ce jalon">
                  <p className="dessous-texte">
                    Un jalon se note à la moyenne simple de ses cinq étapes. Score retenu pour ce jalon :{" "}
                    <strong>{Math.round(moyenneJalon)}</strong> : il en faut{" "}
                    <strong>{SEUIL_JALON_REUSSI}</strong>.{" "}
                    {moyenneJalon >= SEUIL_JALON_REUSSI
                      ? "Tu es au-dessus."
                      : "En dessous, la vidéo est ratée."}
                    {previsionIncomplete
                      ? " Une étape est notée par le serveur : cette prévision est incomplète."
                      : ""}
                  </p>
                  <p className="dessous-texte">
                    Ratée, elle coûte quand même ses{" "}
                    <strong>{Math.round(H_VIDEO_JALON / H_JOUR)} jours</strong>, ne rapporte{" "}
                    <strong>aucun point</strong>, ne rend que {Math.round(JALON_EFFET_RATE * 100)} % de son
                    pourcentage, et <strong>ne se retente pas</strong> : le jalon est consommé.
                  </p>
                  <p className="dessous-texte">
                    Réussie, son effet est un{" "}
                    <strong>
                      pourcentage de tes abonnés du moment ({jalon.effets_reussite.subs_pct_min} à{" "}
                      {jalon.effets_reussite.subs_pct_max} %)
                    </strong>
                    , tiré au hasard dans cette fourchette : ton score décide de la réussite, pas du
                    montant.
                  </p>
                </DessousBloc>
              ) : null}
              {/* La phrase qui manquait : en mode compte, ce que l'écran montre est une prévision. */}
              {surServeur ? (
                <DessousBloc titre="Le calcul">
                  <p className="dessous-texte">
                    Ces notes sont ce que ton navigateur a calculé. Le serveur rejoue tes gestes et
                    refait le calcul lui-même.
                  </p>
                </DessousBloc>
              ) : null}
              <BlocProduction />
              <BlocServeur />
            </LesDessous>
          </div>
        </section>
        {pied}
        {accessibilite}
      </CadreAtelier>
    );
  }

  // ───────────────────────── Une étape ─────────────────────────
  const stage = etapesJouees[index]!;
  const etape = ETAPES[stage];
  const Jeu = MINI_JEUX[stage] ?? CurseurStub;
  const vrai = MINI_JEUX[stage] !== undefined;
  const assistsEtape = assists(state, MINIJEU_DE_LETAPE[stage]);
  // ⟦A-60⟧ Le consommable armé pour cette production (`inventaire.prochaine_prod`), s'il
  // concerne le mini-jeu de l'étape affichée. Un seul peut être armé à la fois.
  const matosArme = contenu()
    .boutique.filter((o) => prochaineProd(state).includes(o.id))
    .find((o) => o.assists.some((a) => a.minigame === MINIJEU_DE_LETAPE[stage]));
  /** L'offre du jour pour CETTE étape — vide si le marché n'y est pas ouvert (§5.6). */
  const offresEtape = marche?.etapes[stage] ?? [];
  const offreDuJour = offresEtape[0]; // triées par prix croissant : la moins chère d'abord

  // ─── ⟦C62 · lot atelier · friction 3⟧ UN écran de consigne, UN « Jouer » ───────────────────────────────────
  // Avant : l'intro de Matt (sa réplique, ses lignes d'info, Jouer, Déléguer, Les dessous), PUIS la consigne du
  // mini-jeu (sa règle, ses touches, Jouer, Les dessous) : 16 clics pour une première vidéo de trois étapes, dont
  // trois « Jouer » qui ne lançaient rien. Désormais l'Atelier ne rend plus que le mini-jeu, et lui passe ce qui
  // venait de l'intro : la réplique de Matt (`accueil`, à la place de la phrase de règle), les boutons d'à côté
  // (`actionsPlus`) et les blocs des dessous (`DessousEnPlus`, ouverts par le « Les dessous » du mini-jeu, le seul
  // de l'écran). Tout cela ne vaut que la première fois que ce mini-jeu se joue, comme l'intro d'avant.
  const nbAssists = Object.keys(assistsEtape).length;
  const accueil =
    premiereFoisCeMinijeu && leconDuStage ? (
      <div className="jeu-accueil">
        {/* ⟦C57 · lot 8 · A-174⟧ UNE réplique, la courte (`court`) : les deux d'origine, le détail et « à retenir »
            sont derrière « Les dessous ». Sans `onSuite` : un clic ne fait qu'accélérer la machine à écrire. */}
        <BoiteDialogue hote={HOTE} texte={leconDuStage.court} />
        {/* ⟦A-129 · C11⟧ L'exigence n'est plus récitée ici : elle est dans le pied, et son code dans les dessous.
            Restent les mentions d'accessibilité, quand elles s'appliquent. */}
        {toleranceActive || reducedMotion ? (
          <p className="ligne-info discret">
            {toleranceActive ? `Tolérance : plafond ${CAP_TOLERANCE}` : ""}
            {toleranceActive && reducedMotion ? " · " : ""}
            {reducedMotion ? `Mode posé : plafond ${CAP_TOLERANCE}` : ""}
          </p>
        ) : null}
        {/* ⟦SPEC-UI §3.3⟧ « assists actifs listés avec leur icône de skill » : ⟦C57⟧ un NOMBRE d'aides à l'écran ;
            la liste, avec ses effets chiffrés (les mêmes mots que l'arbre, `libelles.ts`), est dans les dessous. */}
        {nbAssists > 0 ? (
          <p className="ligne-info">
            {etape.icone} {nbAssists} aide{nbAssists > 1 ? "s" : ""} active{nbAssists > 1 ? "s" : ""}
          </p>
        ) : null}
        {/* ⟦A-60⟧ Le matos armé aide à CETTE vidéo et disparaît avec elle : le nom reste à l'écran, la phrase sur
            sa durée de vie est dans les dessous. */}
        {matosArme === undefined ? null : (
          <p className="ligne-info">
            🎒 Dans le sac : <strong>{matosArme.nom}</strong>
          </p>
        )}
        {!vrai ? <p className="ligne-info discret">Étape provisoire : règle ton score.</p> : null}
      </div>
    ) : undefined;

  const actionsPlus = premiereFoisCeMinijeu ? (
    <>
      {/* ⟦SPEC-UI §3.3⟧ « bouton Déléguer si éligible §5.6 » : l'offre du jour de CETTE étape, au prix que le marché
          affiche, qui est le prix que le moteur débitera. */}
      {offreDuJour !== undefined ? (
        <button
          className="bouton"
          type="button"
          disabled={pending}
          title={`${offreDuJour.nom} : rendu attendu ${offreDuJour.cote} ± ${offreDuJour.variance}${offresEtape.length > 1 ? ` · ${offresEtape.length} personnes disponibles` : ""}`}
          onClick={() => void deleguer(stage, offresEtape)}
        >
          🧑‍🔧 Déléguer : {nombre(offreDuJour.prix)} ₵
          {offresEtape.length > 1 ? ` (${offresEtape.length} offres)` : ` (${offreDuJour.nom})`}
        </button>
      ) : null}
      {scoreDeDev === null ? null : (
        <button
          className="bouton discret"
          type="button"
          // ⟦DEV⟧ Bac à sable : le score posé fait foi. Sur serveur, la timeline soumise est **vide**, le
          // serveur notera ce qui a été joué, c'est-à-dire rien.
          onClick={() => {
            poserScore(stage, scoreDeDev, timelineVide(stage));
            if (index < etapesJouees.length - 1) etapeSuivante();
          }}
        >
          ⏩ score direct {scoreDeDev} (dev)
        </button>
      )}
    </>
  ) : undefined;

  /** Ce que la chaîne fait de ce score : une ligne, dite sous la note de l'écran de fin du mini-jeu. */
  const consequenceDe = (score: number): string | null => {
    if (plafondAccessibilite !== null && score === CAP_TOLERANCE) {
      return `Plafonné à ${CAP_TOLERANCE} (${plafondAccessibilite})`;
    }
    const retenu = retenuDe(stage, score);
    if (retenu >= SEUIL_NOTE_PARFAIT) return "Bonus de viralité !";
    if (retenu < SEUIL_NOTE_CORRECT) return "⚠ Risque de vidéo nulle";
    return null;
  };

  /** Les blocs que l'intro d'avant portait dans ses dessous, désormais dans ceux du mini-jeu (première fois). */
  const dessousPlus = premiereFoisCeMinijeu ? (
    <>
      {nbAssists > 0 ? (
        <DessousBloc titre="Assists actifs">
          <DessousLignes
            lignes={Object.entries(assistsEtape).map(([param, value]) => ({
              icone: etape.icone,
              texte: libelleAssist(param as Parameters<typeof libelleAssist>[0], value),
            }))}
          />
        </DessousBloc>
      ) : null}
      {matosArme === undefined ? null : (
        <DessousBloc titre="Dans le sac">
          <p className="dessous-texte">
            🎒 Dans le sac pour cette vidéo : <strong>{matosArme.nom}</strong>, il aura servi à la publication.
          </p>
        </DessousBloc>
      )}
      {toleranceActive || reducedMotion ? (
        <DessousBloc titre="Accessibilité">
          <p className="dessous-texte">
            {toleranceActive ? `Tolérance active (plafond ${CAP_TOLERANCE}). ` : ""}
            {reducedMotion ? `Mode posé (plafond ${CAP_TOLERANCE}).` : ""}
          </p>
        </DessousBloc>
      ) : null}
      {!vrai ? (
        <DessousBloc titre="Étape provisoire">
          <p className="dessous-texte">Cette étape est encore un stub : tu règles son score toi-même.</p>
        </DessousBloc>
      ) : null}
      {offreDuJour !== undefined ? <BlocDelegation /> : null}
      <BlocProduction />
    </>
  ) : null;

  // Sur serveur, on ne monte AUCUN mini-jeu avant d'avoir son pattern : le laisser se générer
  // localement, ce serait jouer une partition et en faire noter une autre.
  // ⟦C23 §4⟧ `patternsIndispensables` est calculé plus haut désormais (l'effet d'auto-saut de
  // l'intro en a besoin avant le premier retour anticipé du composant), inchangé au caractère
  // près, juste hissé.

  return (
    <CadreAtelier sujet={titreProjet}>
      <EnteteAtelier
        etapes={etapes}
        index={index}
        sujet={titreProjet}
        onAbandon={() => void abandonner()}
        deleguees={embauchesProd}
      />

      <section className="carte zone-jeu">
        {patternsIndispensables ? (
          <div className="jeu jeu-intro">
            <h2 className="jeu-titre">
              {etape.icone} {etape.nom}
            </h2>
            {etatPatterns === "erreur" ? (
              <>
                {/* ⟦C57 · lot 8⟧ Une phrase ; le message du serveur et « sans elle, il n'y a rien à jouer »
                    sont derrière « Les dessous ». */}
                <p className="jeu-regle">Partition indisponible.</p>
                <div className="jeu-actions">
                  <button
                    className="bouton primaire grand"
                    type="button"
                    onClick={() => setEssai((n) => n + 1)}
                  >
                    Réessayer
                  </button>
                  <LesDessous titre="La partition du tour">
                    <DessousBloc>
                      <p className="dessous-texte">
                        Le studio n'a pas pu récupérer la partition de ce tour : {erreurPatterns}
                      </p>
                      <p className="dessous-texte">
                        La partition est tirée par le serveur. Sans elle, il n'y a rien à jouer.
                      </p>
                    </DessousBloc>
                  </LesDessous>
                </div>
              </>
            ) : (
              <p className="jeu-regle">Partition en route…</p>
            )}
          </div>
        ) : null}

        {/* ⟦A-129 · C11⟧ Plus de voile à attendre : le jeu se monte directement, chrono compris, comme
            n'importe quelle étape déjà vue. ⟦C62 · lot atelier · friction 3⟧ Et c'est lui, avec SA consigne, le seul
            écran de lancement : la réplique de Matt (première fois) y tient la place de la phrase de règle. */}
        {!patternsIndispensables && (phase === "jeu" || phase === "intro") ? (
          <DessousEnPlus.Provider value={dessousPlus}>
            <Jeu
              key={stage}
              stage={stage}
              state={stateAtelier}
              format={brouillon.format}
              seed={run.seed}
              pattern={patterns}
              alignementSujet={alignementSujet}
              // ⟦A-3⟧ Ce que les étapes d'avant ont réussi voyage jusqu'à La Découpe.
              scoresPrecedents={etapesJouees
                .slice(0, index)
                .map((precedent) => scores[precedent] ?? 0)}
              // ⟦A-9⟧ **Le geste n'est jamais saboté.** On enregistre le score BRUT du mini-jeu :
              // le plafond est une borne de ce que ton niveau en retient, pas une correction de
              // ce que tu as joué. C'est le moteur qui plafonne, à la publication
              // (`scoresEffectifs`) — l'écran, lui, montre les deux.
              //
              // ⟦C62 · lot atelier · friction 3⟧ **« Suivant » : garder ET passer à l'étape d'après.** Avant, « Garder
              // ce script » posait le score, puis un second écran (le score, une phrase, « Étape suivante ») demandait
              // un clic de plus. Le dernier mini-jeu garde son comportement : son score posé bascule dans le récap.
              // La leçon de Matt est marquée lue ici, à la fin de la première partie de ce mini-jeu.
              onFini={(score, timeline) => {
                if (decouverteActive && leconDuStage) marquerLeconVue(leconDuStage.id);
                poserScore(stage, score, timeline);
                if (index < etapesJouees.length - 1) etapeSuivante();
              }}
              accueil={accueil}
              actionsPlus={actionsPlus}
              consequence={consequenceDe}
            />
          </DessousEnPlus.Provider>
        ) : null}

        {/* ⟦A-129 · C11⟧ **Score + récap fusionnés, par mini-jeu.** Un seul écran désormais :
            le score, UNE ligne de verdict, le bouton étape suivante — plus de plafond
            d'accessibilité à part, plus d'historique à part, et plus de mention « ta chaîne en
            exploite N aujourd'hui » (cette pédagogie vit dans le guide). Cette branche ne rend
            plus jamais le DERNIER mini-jeu de la séquence : sa fin bascule directement dans le
            récap final ci-dessus, qui fusionne les deux écrans pour de bon.
            ⟦C62 · lot atelier · friction 3⟧ **Et elle ne se rend plus du tout en jeu normal** : le « Suivant » de
            l'écran de fin du mini-jeu garde le score ET passe à l'étape d'après (`onFini`, plus haut). Elle reste pour
            une production reprise d'une sauvegarde qui s'était arrêtée sur cet écran (`etatRestaure`) : même note
            sur 100, même mot, un seul « Suivant ». */}
        {!patternsIndispensables && phase === "score" ? (
          <div className="jeu jeu-score">
            <h2 className="jeu-titre">
              {etape.icone} {etape.nom}
            </h2>
            {typeof scores[stage] === "number" ? (
              <NoteSur100 note={scores[stage] ?? 0} consequence={consequenceDe(scores[stage] ?? 0)} />
            ) : (
              <>
                <p className="score-gros">?</p>
                {/* ⟦C57 · lot 8⟧ Quatre mots ; la phrase d'avant est derrière « Les dessous ». */}
                <p className="jeu-seuils">Notée par le serveur.</p>
                <LesDessous titre={`${etape.nom} : le score`}>
                  <DessousBloc>
                    <p className="dessous-texte">
                      Cette étape est notée par le serveur : il a gardé pour lui de quoi la juger.
                    </p>
                  </DessousBloc>
                </LesDessous>
              </>
            )}
            <button className="bouton primaire" type="button" onClick={etapeSuivante}>
              Suivant
            </button>
          </div>
        ) : null}
      </section>
      {pied}
      {accessibilite}
    </CadreAtelier>
  );
}

/**
 * ⟦A-5 proposition 7⟧ Ta courbe à toi : les 5 derniers scores de chaque étape, tels que le
 * moteur les a gardés (`state.atelier_history`). Le seul « tu progresses » que le jeu
 * s'autorise, parce que c'est le seul qui ne ment pas.
 */
function Progression({
  state,
  etapes,
}: {
  state: GameState;
  etapes: readonly AtelierStage[];
}) {
  const lignes = etapes
    .map((stage) => [stage, (state.atelier_history[stage] ?? []).slice(-5)] as const)
    .filter(([, scores]) => scores.length >= 2);
  if (lignes.length === 0) return null;
  return (
    <div className="progression-joueur">
      <h3 className="bloc-titre">Ta main s'améliore</h3>
      {lignes.map(([stage, scores]) => (
        <p className="ligne-info discret" key={stage}>
          {ETAPES[stage].icone} {ETAPES[stage].nom.toLowerCase()} : {scores.join(" → ")}
        </p>
      ))}
    </div>
  );
}

// ⟦C57 · lot 8 · A-174⟧ La règle en UNE phrase (SPEC-UI §3.3) vit dans `atelier/core/textes.ts` : la forme
// COURTE (`REGLES_COURTES`) est permanente dans le pied, la forme d'avant (`REGLES_LONGUES`) est derrière
// « Les dessous » de l'intro d'étape.

function EnteteAtelier({
  etapes,
  index,
  sujet,
  onAbandon,
  deleguees = {},
}: {
  etapes: readonly AtelierStage[];
  /** Rang dans les étapes JOUÉES (les embauchées ne comptent pas). */
  index: number;
  sujet: string;
  onAbandon(): void;
  /** ⟦A-4 E · M3.30⟧ étape → freelance : la pastille dit « confiée », pas « à jouer ». */
  deleguees?: Partial<Record<AtelierStage, string>>;
}) {
  // Le rang JOUÉ de chaque étape non déléguée — c'est lui que `index` compare.
  let rangJoue = -1;
  return (
    <header className="entete entete-atelier">
      {/* ⟦C48 · A-168 §3⟧ « L'Atelier » et le sujet sont dans le bandeau de la fenêtre
          (`CadreAtelier.tsx`) : l'en-tête ne garde que la progression et la sortie. */}
      <div className="pastilles" aria-label={`Progression de la production de « ${sujet} »`}>
        {etapes.map((stage) => {
          if (deleguees[stage] !== undefined) {
            return (
              <span
                key={stage}
                className="pastille faite"
                title={`${ETAPES[stage].nom} — confiée à un freelance`}
              >
                🧑‍🔧
              </span>
            );
          }
          rangJoue += 1;
          const etat = rangJoue < index ? "faite" : rangJoue === index ? "courante" : "";
          return (
            <span key={stage} className={`pastille ${etat}`} title={ETAPES[stage].nom}>
              {ETAPES[stage].icone}
            </span>
          );
        })}
      </div>
      <div className="entete-droite">
        <button className="bouton discret" type="button" onClick={onAbandon}>
          Jeter le projet
        </button>
      </div>
    </header>
  );
}

/**
 * ⟦DEV⟧ `?devScore=80` — passer une étape sans jouer la boucle temporisée.
 *
 * **Pourquoi ça existe** : tous nos harnais de vérification tournent dans un navigateur en
 * `document.hidden`, où `requestAnimationFrame` rend **0 frame** (mesuré : 0 sur 1,2 s). Aucun
 * mini-jeu ne peut s'y terminer, donc l'écran de publication et ses coulisses étaient
 * **inatteignables** en test — un angle mort qui a déjà fait livrer du code non vérifié.
 *
 * **Trois verrous, et ils comptent** :
 *  1. `import.meta.env.DEV` — le bloc est supprimé du bundle de production par Vite ;
 *  2. il faut le paramètre explicite dans l'URL, donc rien ne change pour un joueur normal ;
 *  3. ça ne triche que dans le **bac à sable**. Le serveur ne lit JAMAIS un score envoyé par le
 *     client : il recalcule tout depuis la timeline (SPEC-API §6.4). Sur une run de compte, ce
 *     raccourci soumet donc une timeline **vide** et rapporte ce qu'une timeline vide vaut — il
 *     ne peut pas devenir une porte dérobée, il n'y a pas de porte de ce côté.
 */
const CLE_DEV_SCORE = "maxime:devScore";

function lireScoreDeDev(): number | null {
  if (!import.meta.env.DEV) return null;
  // Le paramètre ne survit pas à la navigation interne de React Router (l'Atelier est une
  // route à part), donc on le mémorise pour l'onglet dès qu'on le voit. `sessionStorage` et
  // pas `localStorage` : fermer l'onglet doit suffire à revenir au jeu normal.
  const brut = new URLSearchParams(window.location.search).get("devScore");
  if (brut !== null) {
    if (brut === "" || brut === "off") {
      window.sessionStorage.removeItem(CLE_DEV_SCORE);
      return null;
    }
    window.sessionStorage.setItem(CLE_DEV_SCORE, brut);
  }
  const garde = window.sessionStorage.getItem(CLE_DEV_SCORE);
  if (garde === null) return null;
  const n = Number(garde);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : null;
}
